import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, Params, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { isNil } from 'lodash';
import { Observable } from 'rxjs';
import { filter, map, mergeMap, startWith, switchMap, tap } from 'rxjs/operators';

import {
  selectedDashboardViewType,
  setCustomBreadcrumbs,
} from '@main-application/dashboards/actions/dashboard.actions';
import { DashboardsAbstract } from '@main-application/dashboards/components/abstract/dashboards.abstract';
import { selectCustomBreadcrumbs } from '@main-application/dashboards/selectors/dashboards.selectors';
import { loadAllUserPortfolios } from '@portfolio/actions/portfolio.actions';
import { loadAllUserProperties } from '@shared/actions/user.actions';
import { DashboardViewType } from '@shared/enums/dashboard-view-type';
import { EIcon } from '@shared/enums/icon.enum';
import { MainPageActions } from '@shared/enums/main-page-actions';
import { RouteData } from '@shared/enums/route-data';
import { BreadcrumbItem } from '@shared/interfaces/breadcrumb-item';
import { EnumerationItem } from '@shared/interfaces/enumeration-item';
import { UserData } from '@shared/interfaces/user-data';
import { selectDashboardViewType } from '@shared/selectors/enumeration.selectors';
import { selectIsAuthenticated, selectUserData } from '@shared/selectors/user.selectors';

@UntilDestroy()
@Component({
  selector: 'app-top-panel',
  templateUrl: './top-panel.component.html',
  styleUrls: ['./top-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TopPanelComponent extends DashboardsAbstract implements OnInit, OnDestroy {
  breadcrumbList: BreadcrumbItem[] = [];
  routeBreadcrumbList: BreadcrumbItem[] = [];
  additionalBreadcrumbList: BreadcrumbItem[] = [];
  userData: UserData;

  @Input() isSidePanelExpanded: boolean;
  @Output() mainPageActionEvent: EventEmitter<MainPageActions> = new EventEmitter<MainPageActions>();

  constructor(
    protected cdr: ChangeDetectorRef,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private store: Store<{}>
  ) {
    super(cdr);
  }

  ngOnInit(): void {
    this.router.events
      .pipe(
        untilDestroyed(this),
        filter(event => event instanceof NavigationEnd),
        startWith(null),
        tap((event: NavigationEnd) => {
          this.routeBreadcrumbList = this.getBreadcrumbList(this.activatedRoute.root);
          this.store.dispatch(setCustomBreadcrumbs({ breadcrumbList: [] }));
          this.mergeBreadcrumbList();
        }),
        map(() => this.activatedRoute),
        map(route => {
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
        mergeMap(route => {
          return route.data;
        }),
        tap(data => {
          const newDashboardViewType = (
            isNil(data?.[RouteData.DASHBOARD_VIEW_TYPE])
              ? DashboardViewType.NONE
              : data?.[RouteData.DASHBOARD_VIEW_TYPE]
          ) as DashboardViewType;

          if (this.dashboardViewType !== newDashboardViewType) {
            this.dashboardViewType = newDashboardViewType;
            this.cdr.detectChanges();
          }
        }),
        switchMap(() => this.getDashboardViewTypes())
      )
      .subscribe();

    this.store
      .select(selectCustomBreadcrumbs)
      .pipe(
        untilDestroyed(this),
        tap((breadcrumbList: BreadcrumbItem[]) => {
          this.additionalBreadcrumbList = breadcrumbList;
          this.mergeBreadcrumbList();
          this.cdr.detectChanges();
        })
      )
      .subscribe();

    this.store
      .select(selectUserData)
      .pipe(
        untilDestroyed(this),
        filter((userData: UserData) => !!userData),
        tap((userData: UserData) => {
          this.userData = userData;
          this.cdr.detectChanges();
        })
      )
      .subscribe();

    this.store
      .select(selectIsAuthenticated)
      .pipe(
        untilDestroyed(this),
        tap((isAuthenticated: boolean) => {
          this.isAuthenticated = isAuthenticated;
          this.cdr.detectChanges();
        }),
        filter(isAuthenticated => isAuthenticated)
      )
      .subscribe(() => {
        this.store.dispatch(loadAllUserProperties());
        this.store.dispatch(loadAllUserPortfolios());
      });
  }

  ngOnDestroy(): void {}

  menuAction(action: MainPageActions) {
    this.mainPageActionEvent.emit(action);
  }

  home() {
    this.menuAction(MainPageActions.HOME_PAGE);
  }

  private getBreadcrumbList(
    activatedRoute: ActivatedRoute,
    url = '',
    breadcrumbs: BreadcrumbItem[] = []
  ): BreadcrumbItem[] {
    if (!activatedRoute) {
      return [];
    }

    const icon = (activatedRoute.routeConfig?.data?.[RouteData.BREADCRUMB_ICON] as EIcon) || null;
    const label = activatedRoute.routeConfig?.data?.[RouteData.BREADCRUMB] || null;
    let path = activatedRoute.routeConfig?.path || null;
    if (path) {
      const lastRoutePart = path.split('/').pop();
      const isDynamicRoute = lastRoutePart.startsWith(':');
      if (isDynamicRoute && !!activatedRoute.snapshot) {
        path = path.replace(lastRoutePart, activatedRoute.snapshot.params[lastRoutePart.split(':')[1]]);
      }
      url = `${url}/${path}`;
    }

    if (label) {
      breadcrumbs.push({ label, url, icon });
    }

    if (activatedRoute.firstChild) {
      this.getBreadcrumbList(activatedRoute.firstChild, url, breadcrumbs);
    }
    return breadcrumbs;
  }

  private getDashboardViewTypes(): Observable<EnumerationItem> {
    return this.store.select(selectDashboardViewType).pipe(
      untilDestroyed(this),
      filter(dashboardViewType => !!dashboardViewType),
      map(dashboardViewTypes => dashboardViewTypes.find(e => e.enumerationValue == this.dashboardViewType)),
      tap((dashboardViewTypeEnum: EnumerationItem) => {
        this.store.dispatch(selectedDashboardViewType({ dashboardViewType: dashboardViewTypeEnum }));
        this.cdr.detectChanges();
      })
    );
  }

  private mergeBreadcrumbList() {
    this.breadcrumbList = [...this.routeBreadcrumbList, ...this.additionalBreadcrumbList];
    this.cdr.detectChanges();
  }

  private updateRouterParam(params: Params) {
    const currentParams = this.activatedRoute.snapshot.queryParams;
    const currentFragment = this.activatedRoute.snapshot.fragment;
    this.router
      .navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: { ...currentParams, ...params },
        queryParamsHandling: 'merge',
        replaceUrl: true,
        fragment: currentFragment,
      })
      .then();
  }
}
